import React, { useEffect, useState } from 'react';
import DeveloperList from './components/DeveloperList';
import HowItWorks from './components/HowItWorks';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import CustomerReferences from './components/CustomerReferences';
// import FAQ from './components/FAQ';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import EmailCollectionSection from './components/EmailCollectionSection';
import { SEO } from './components/SEO/SEO';
import { StructuredData } from './components/SEO/StructuredData';

import SuccessModal from './components/SuccessModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import { initializeAnalytics, trackPageView } from './utils/analytics';
import { Developer } from './types/developer';

function App() {
  // Moved from DeveloperList: State for selected developers and modals
  const [selectedDevelopers, setSelectedDevelopers] = useState<Developer[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  // Header search state
  const [headerSearchTerm, setHeaderSearchTerm] = useState('');
  // Highlight state for selection feedback
  const [highlightSelection, setHighlightSelection] = useState(false);

  // Moved from DeveloperList: Selection handlers
  const handleSelectDeveloper = (developer: Developer) => {
    try {
      if (!developer || !developer.id) {
        console.error('Invalid developer object passed to selection:', developer);
        return;
      }
      
      // Check if developer is already selected to prevent duplicates
      const isAlreadySelected = selectedDevelopers.some(dev => dev.id === developer.id);
      if (isAlreadySelected) {
        console.log('Developer already selected:', developer.name);
        return;
      }
      
      console.log('Selecting developer:', developer.name);
      setSelectedDevelopers(prev => [...prev, developer]);
    } catch (error) {
      console.error('Error in handleSelectDeveloper:', error);
    }
  };

  const handleDeselectDeveloper = (developerId: string) => {
    setSelectedDevelopers(prev => prev.filter(dev => dev.id !== developerId));
  };

  const handleClearSelection = () => {
    setSelectedDevelopers([]);
  };

  // Header search handler
  const handleHeaderSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderSearchTerm(e.target.value);
  };

  const handleSelectAll = (developers: Developer[]) => {
    setSelectedDevelopers([...developers]);
  };

  const handleDeselectAll = () => {
    setSelectedDevelopers([]);
  };

  // Direct success handler for email sending
  const handleEmailSuccess = () => {
    setShowSuccessModal(true);
    setSelectedDevelopers([]); // Clear selection after successful email sending
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleOpenPrivacyModal = () => {
    setShowPrivacyModal(true);
  };

  const handleClosePrivacyModal = () => {
    setShowPrivacyModal(false);
  };

  // Handle highlighting selection area when validation fails
  const handleHighlightSelection = () => {
    setHighlightSelection(true);
    setTimeout(() => setHighlightSelection(false), 1000);
  };

  useEffect(() => {
    try {
      // Initialize analytics (only if environment variables are set)
      initializeAnalytics();
      
      // Track page view
      trackPageView('Home');
    } catch (error) {
      console.log('Analytics initialization skipped - environment variables not configured');
    }
  }, []);

  return (
    <ErrorBoundary>
      {/* SEO Meta Tags */}
      <SEO />

      {/* Structured Data for Google */}
      <StructuredData type="organization" />
      <StructuredData type="website" />

      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* New Unified Hero Container - Active */}
        <div className="relative bg-gray-900 overflow-hidden h-36 sm:h-44 lg:h-52 flex flex-col">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="/images/Titleline.png" 
              alt="Ukrainian developers" 
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-opacity-30"></div>
          </div>
          
          {/* New Header Content with Backdrop */}
          <div className="relative z-20 w-full bg-black/20 backdrop-blur-sm py-3 flex-shrink-0">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex justify-between items-center">
                {/* Left side: Logo and Site Name */}
                <div className="flex items-center space-x-3">
                  <img 
                    src="/images/utvecklaredirekt_logo.png" 
                    alt="Utvecklare Direkt" 
                    className="h-10 sm:h-12 lg:h-14 w-auto"
                  />
                  <div className="flex flex-col">
                    <span className="text-white/80 text-xs lg:text-sm hidden sm:block">Vi förmedlar ukrainska utvecklare per timme</span>
                  </div>
                </div>

                {/* Right side: Om oss Link */}
                <div className="flex items-center">
                  <button 
                    onClick={() => {
                      // Look for the About Us section by finding the section with "Om oss" heading
                      const aboutSection = Array.from(document.querySelectorAll('section')).find(section => 
                        section.textContent?.includes('Om oss')
                      );
                      if (aboutSection) {
                        aboutSection.scrollIntoView({ 
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }}
                    className="text-white hover:text-gray-200 font-medium text-sm transition-colors duration-200"
                  >
                    Om oss
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Banner Text Content centered in remaining space */}
          <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-2 sm:py-3">
            <div className="text-center space-y-1 sm:space-y-2 max-w-6xl mx-auto">
              {/* Main Headline */}
              <h1 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold leading-tight">
                <span className="inline-block bg-white/95 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-primary-200 font-bold text-gray-900 shadow-2xl">
                  Ladda ner CV'n på utvecklare som kan börja imorgon
                </span>
              </h1>
              

            </div>
          </div>
        </div>

        
        {/* Main Content - Unified Container for Developer List and Email Collection */}
        <div className="flex-1 flex flex-col">
          {/* Unified Container - Search, Table, and Email Collection */}
          <div className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50">
            {/* Search Section - Integrated into unified container */}
            <div className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 px-6 py-4">
              <div className="max-w-6xl mx-auto">
                <div className="max-w-2xl mx-auto">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={headerSearchTerm}
                      onChange={handleHeaderSearchChange}
                      placeholder="Sök utvecklare efter namn, erfarenhet eller kompetens..."
                      className="block w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Developer List Section */}
            <div className="w-full">
              <DeveloperList 
                selectedDevelopers={selectedDevelopers}
                onSelectDeveloper={handleSelectDeveloper}
                onDeselectDeveloper={handleDeselectDeveloper}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onClearSelection={handleClearSelection}
                headerSearchTerm={headerSearchTerm}
                highlightSelection={highlightSelection && selectedDevelopers.length === 0}
              />
            </div>
            
            {/* Email Collection Section - Integrated without separator */}
            <div className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 px-6 py-4">
              <div className="max-w-6xl mx-auto">
              {/* Selected developers compact display */}
              {selectedDevelopers.length > 0 && (
                <div className={`text-center mb-3 transition-all duration-300 ${
                  highlightSelection 
                    ? 'ring-2 ring-blue-500 ring-opacity-50 bg-blue-50 rounded-lg p-2 animate-pulse' 
                    : ''
                }`}>
                  <div className="flex flex-wrap justify-center gap-1 max-w-4xl mx-auto">
                    {selectedDevelopers.slice(0, 4).map((dev) => (
                      <span 
                        key={dev.id} 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm ${
                          highlightSelection 
                            ? 'bg-blue-200 text-blue-900' 
                            : 'bg-primary-100 text-primary-800'
                        }`}
                      >
                        {dev.name}
                      </span>
                    ))}
                    {selectedDevelopers.length > 4 && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm ${
                        highlightSelection 
                          ? 'bg-blue-200 text-blue-900' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        +{selectedDevelopers.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Email Collection Form */}
              <div className="bg-white rounded-lg shadow-md p-3 sm:p-4">
                <EmailCollectionSection
                  selectedDevelopers={selectedDevelopers}
                  onSuccess={handleEmailSuccess}
                  onOpenPrivacyModal={handleOpenPrivacyModal}
                  isLoading={false}
                  onHighlightSelection={handleHighlightSelection}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Marketing Sections */}
        <div className="flex-shrink-0">
        <HowItWorks />
        <CustomerReferences />
        <AboutUs />
        <ContactUs onOpenPrivacyModal={handleOpenPrivacyModal} />
        {/* <FAQ /> */}
        </div>
        </div>
        
        {/* Footer */}
        <Footer onOpenPrivacyModal={handleOpenPrivacyModal} />
        
        {/* Modals */}
        {showSuccessModal && (
          <SuccessModal onClose={handleCloseSuccessModal} />
        )}
        
        {showPrivacyModal && (
          <PrivacyPolicyModal onClose={handleClosePrivacyModal} />
        )}
        
        <CookieConsent />
      </div>
    </ErrorBoundary>
  );
}

export default App;
